import React from 'react';

export enum ToolsPanelTab {
  BALookup = 0,
  LocationAgreementHistory = 1,
  LicenseeHistory = 2,
}

type UIMapType = {
  isSidePanelExpanded: boolean;
  isMobileDrawerOpen: boolean;
  isToolsPanelOpen: boolean;
  toolsPanelTab: ToolsPanelTab;
  handleToggleSidePanel: () => void;
  handleToggleDrawer: () => void;
  handleCloseDrawer: () => void;
  handleToggleToolsPanel: () => void;
  handleToolsPanelTabChange: (tab: ToolsPanelTab) => void;
};

function getStateFromLocalStorage(): boolean {
  return JSON.parse(localStorage.getItem('isSidePanelExpanded') || 'true');
}

const UIStateContext = React.createContext<UIMapType>({
  isSidePanelExpanded: true,
  isToolsPanelOpen: false,
  toolsPanelTab: ToolsPanelTab.BALookup,
  handleToggleSidePanel: () => {},
  isMobileDrawerOpen: false,
  handleToggleDrawer: () => {},
  handleCloseDrawer: () => {},
  handleToggleToolsPanel: () => {},
  handleToolsPanelTabChange: () => {},
});

export const UIStateProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isSidePanelExpanded, setIsSidePanelExpanded] = React.useState(
    getStateFromLocalStorage()
  );
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = React.useState(false);
  const [toolsPanelTab, setToolsPanelTab] = React.useState(
    ToolsPanelTab.BALookup
  );

  const handleToggleDrawer = React.useCallback(
    () => setIsMobileDrawerOpen((prevState) => !prevState),
    []
  );
  const handleCloseDrawer = React.useCallback(
    () => setIsMobileDrawerOpen(false),
    []
  );
  const handleToggleSidePanel = React.useCallback(
    () => setIsSidePanelExpanded((prevState) => !prevState),
    []
  );

  const handleToggleToolsPanel = React.useCallback(
    () => setIsToolsPanelOpen((prevState) => !prevState),
    []
  );

  const handleToolsPanelTabChange = React.useCallback(
    (tab: ToolsPanelTab) => setToolsPanelTab(tab),
    []
  );

  const value = React.useMemo(
    () => ({
      isSidePanelExpanded,
      isMobileDrawerOpen,
      isToolsPanelOpen,
      toolsPanelTab,
      handleToggleSidePanel,
      handleToggleDrawer,
      handleCloseDrawer,
      handleToggleToolsPanel,
      handleToolsPanelTabChange,
    }),
    [
      isSidePanelExpanded,
      isMobileDrawerOpen,
      isToolsPanelOpen,
      toolsPanelTab,
      handleToggleSidePanel,
      handleToggleDrawer,
      handleCloseDrawer,
      handleToggleToolsPanel,
      handleToolsPanelTabChange,
    ]
  );

  return (
    <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>
  );
};

export const useUI = (): UIMapType => {
  const context = React.useContext(UIStateContext);
  if (!context) {
    throw new Error('useUI must be used within an UIStateProvider');
  }
  return context;
};
